import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserData } from "@/pages/Index";
import { decorateShopUrl } from "@/lib/tracking";

interface InvestmentProps {
  userData: UserData;
  onBack: () => void;
  
}

const getConcernReasoning = (concern: string) => {
  const map: Record<string, { why: string; benefit: string }> = {
    wrinkles: {
      why: "Rimpels ontstaan doordat de dermis minder collageen aanmaakt. Crèmes bereiken deze laag niet.",
      benefit: "Adelira stimuleert collageenproductie in de dermis, waardoor rimpels van binnenuit worden opgevuld.",
    },
    firmness: {
      why: "Slappe huid komt door verlies van elastine en collageen in de diepere huidlagen.",
      benefit: "Adelira herstelt de stevigheid door de natuurlijke collageenproductie weer op gang te brengen.",
    },
    dullness: {
      why: "Een doffe teint ontstaat wanneer de celvernieuwing vertraagt en de huid minder voeding krijgt vanuit de dermis.",
      benefit: "Adelira activeert celvernieuwing op dermisniveau, waardoor je huid weer gaat stralen.",
    },
    all: {
      why: "Al deze klachten hebben dezelfde oorzaak: dalend collageen in de dermis.",
      benefit: "Adelira pakt de kern aan door collageenproductie te stimuleren, wat al je klachten tegelijk verbetert.",
    },
  };
  return map[concern] || map["all"];
};

const getTimelinePhases = (concern: string) => {
  const map: Record<string, { month: string; title: string; desc: string; icon: string }[]> = {
    wrinkles: [
      { month: "Maand 1", title: "Activatie", desc: "Celvernieuwing start. Je huid voelt gladder en de eerste fijne lijntjes worden zachter.", icon: "1" },
      { month: "Maand 2", title: "Doorbraak", desc: "Collageenproductie stijgt. Rimpels worden zichtbaar minder diep.", icon: "2" },
      { month: "Maand 3", title: "Resultaat", desc: "Fijne lijntjes zijn opgevuld. Je huid ziet er jonger en gladder uit.", icon: "3" },
    ],
    firmness: [
      { month: "Maand 1", title: "Activatie", desc: "Huidcellen worden gestimuleerd. Je huid voelt steviger en strakker aan.", icon: "1" },
      { month: "Maand 2", title: "Doorbraak", desc: "Elastine en collageen herstellen. De kaakslijn en wangen worden zichtbaar steviger.", icon: "2" },
      { month: "Maand 3", title: "Resultaat", desc: "Merkbaar liftend effect. Je huid heeft haar natuurlijke stevigheid terug.", icon: "3" },
    ],
    dullness: [
      { month: "Maand 1", title: "Activatie", desc: "Celvernieuwing komt op gang. Je huid voelt gehydrateerder en frisser.", icon: "1" },
      { month: "Maand 2", title: "Doorbraak", desc: "Doffe, grauwe tint verdwijnt. Je huid begint weer te stralen.", icon: "2" },
      { month: "Maand 3", title: "Resultaat", desc: "Stralende, gezond ogende huid. Natuurlijke glow is volledig hersteld.", icon: "3" },
    ],
    all: [
      { month: "Maand 1", title: "Activatie", desc: "Celvernieuwing start op alle niveaus. Je huid voelt zachter, steviger en gehydrateerder.", icon: "1" },
      { month: "Maand 2", title: "Doorbraak", desc: "Rimpels worden zachter, de huid wordt steviger en je teint begint te stralen.", icon: "2" },
      { month: "Maand 3", title: "Resultaat", desc: "Volledig resultaat: gladder, steviger en stralender. Al je huidklachten zijn zichtbaar verbeterd.", icon: "3" },
    ],
  };
  return map[concern] || map["all"];
};

const getUserAge = (ageRange: string): number => {
  const map: Record<string, number> = {
    "25–35": 30, "35–45": 40, "45–55": 50, "55–65": 60, "65–75": 70, "75–85": 80, "85–95": 90,
  };
  return map[ageRange] || 55;
};

const getCollagenAt = (age: number): number => {
  if (age <= 25) return 100;
  if (age <= 50) return Math.round(100 - (age - 25) * 1);
  const at50 = 75;
  return Math.round(Math.max(10, at50 - (age - 50) * 2));
};

const Investment = ({ userData, onBack }: InvestmentProps) => {
  const [phase, setPhase] = useState(0);
  const reasoning = getConcernReasoning(userData.concern);
  const timelinePhases = getTimelinePhases(userData.concern);
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 400),
      setTimeout(() => setPhase(2), 2000),
      setTimeout(() => setPhase(3), 4000),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-foreground/80 font-sans text-lg md:text-xl mb-5 md:mb-8 hover:text-foreground transition-colors cursor-pointer px-3 py-2 -ml-3 rounded-lg hover:bg-secondary/50"
      >
        <span className="text-xl">←</span> Terug
      </button>

      {/* Product image */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="mb-8 rounded-2xl overflow-hidden shadow-warm border border-border"
      >
        <img
          src="https://cdn.shopify.com/s/files/1/0698/6416/1465/files/3_415cce34-f8db-4406-9b33-c77450e56857.png?v=1771609137"
          alt="SkinFuse® Micro-Infusion System"
          className="w-full h-auto"
          loading="eager"
        />
      </motion.div>

      {/* Personalized recommendation header */}
      <div className="text-center mb-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-sans text-sm uppercase tracking-wider text-accent font-bold mb-3"
        >
          Jouw persoonlijke aanbeveling
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-3 leading-tight"
        >
          SkinFuse® Micro-Infusion System
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-foreground/80 font-sans text-lg md:text-xl"
        >
          De eerste thuisbehandeling die de dermis bereikt
        </motion.p>
      </div>

      {/* WHY this product for YOUR concern */}
      <AnimatePresence>
        {phase >= 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="insight-card text-left">
              <p className="font-sans text-sm uppercase tracking-wider text-accent font-bold mb-3">
                Waarom dit product voor jou
              </p>
              <p className="font-sans text-lg text-foreground/80 mb-2 leading-relaxed">
                Op jouw leeftijd ({userData.age}) is je natuurlijke collageenproductie nog maar <span className="text-primary font-bold">{getCollagenAt(getUserAge(userData.age))}%</span> van het oorspronkelijke niveau.
              </p>
              <p className="font-sans text-lg text-foreground/80 mb-4 leading-relaxed">
                {reasoning.why}
              </p>
              <p className="font-serif text-xl text-foreground leading-snug">
                {reasoning.benefit}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase 2: Timeline */}
      <AnimatePresence>
        {phase >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="insight-card text-left mb-6">
              <p className="font-sans text-sm uppercase tracking-wider text-accent font-bold mb-3">
                Hoe het werkt
              </p>
              <div className="space-y-3">
                {[
                  "Krachtige ingrediënten diep in de dermis",
                  "300% meer collageenproductie",
                  "Aanbevolen door Nederlandse dermatologen",
                  "Veilig voor gevoelige huid",
                  "Betaalt zichzelf terug na 1x gebruik",
                ].map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 * i }}
                    className="flex items-center gap-3 text-lg font-sans text-foreground"
                  >
                    <span className="text-accent text-lg flex-shrink-0">✓</span> {item}
                  </motion.div>
                ))}
              </div>
            </div>

            <p className="text-center font-serif text-xl md:text-2xl text-foreground mb-5">
              Wat je kunt verwachten
            </p>
            <div className="space-y-4">
              {timelinePhases.map((tp, i) => (
                <motion.div
                  key={tp.month}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 * i }}
                  className="insight-card text-left flex items-start gap-5 p-5"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-rose-solid flex items-center justify-center text-primary-foreground text-lg">
                    {tp.icon}
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-sans text-sm uppercase tracking-wider text-accent font-bold">
                        {tp.month}
                      </span>
                      <span className="font-serif font-semibold text-foreground text-xl">
                        {tp.title}
                      </span>
                    </div>
                    <p className="text-foreground/80 font-sans text-base leading-relaxed">
                      {tp.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase 3: Price comparison + CTA */}
      <AnimatePresence>
        {phase >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-center font-serif text-xl md:text-2xl text-foreground mb-5">
              De investering
            </p>

            <div className="grid md:grid-cols-2 gap-5 mb-8">
              {/* Kliniek */}
              <div className="bg-card rounded-2xl p-6 border border-border relative opacity-70">
                <div className="absolute -top-3 left-5">
                  <span className="bg-secondary text-foreground/80 font-sans text-sm px-3 py-1 rounded-full font-medium">
                    Kliniekbehandeling
                  </span>
                </div>
                <div className="pt-4">
                  <p className="text-foreground/50 font-sans text-base mb-1">Per sessie</p>
                  <p className="font-serif text-3xl text-foreground/60 mb-4">€150 tot €300</p>
                  <div className="space-y-2 mb-4">
                    {["12 sessies nodig", "Reistijd & wachttijd", "Hersteltijd per sessie"].map((item) => (
                      <div key={item} className="flex items-center gap-3 text-base font-sans text-foreground/50">
                        <span className="text-destructive">✗</span> {item}
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-border pt-3">
                    <p className="text-foreground/50 font-sans text-sm mb-1">Totaal</p>
                    <p className="font-serif text-2xl text-foreground/60 line-through decoration-destructive/40">
                      €1.800 tot €3.600
                    </p>
                  </div>
                </div>
              </div>

              {/* Adelira */}
              <div
                className="rounded-2xl p-6 border-2 shadow-gold relative scale-[1.02]"
                style={{ borderColor: "hsl(38, 55%, 45%)", background: "linear-gradient(135deg, hsl(38 40% 97%), hsl(38 35% 93%))" }}
              >
                <div className="absolute -top-3 left-5">
                  <span className="font-sans text-sm px-4 py-1 rounded-full font-semibold text-white bg-gradient-rose-solid">
                    Adelira Thuis
                  </span>
                </div>
                <div className="absolute -top-3 right-5">
                  <span className="font-sans text-xs px-3 py-1.5 rounded-full font-semibold text-white" style={{ background: "hsl(38, 55%, 40%)" }}>
                    Aanbevolen
                  </span>
                </div>
                <div className="pt-4">
                  <p className="text-foreground/80 font-sans text-base mb-1">
                    Per behandeling
                  </p>
                  <div className="flex items-baseline gap-3 mb-1">
                    <p className="font-serif text-4xl text-foreground font-bold">€19</p>
                    <span className="font-sans text-sm font-semibold px-2 py-0.5 rounded-full text-white bg-gradient-rose-solid">43% korting</span>
                  </div>
                  <p className="text-foreground/75 font-sans text-sm mb-1">
                    3 maanden voorraad · 6 behandelingen
                  </p>
                  <div className="space-y-2 mb-4">
                    {["Thuis, op jouw tempo", "Geen naalden of pijn", "94% ziet verbetering na 6 behandelingen", "Gratis persoonlijk behandelplan"].map((item) => (
                      <div key={item} className="flex items-center gap-3 text-base font-sans text-foreground font-medium">
                        <span className="text-accent">✓</span> {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonials — social proof */}
            <div className="mb-8">
              <p className="text-center font-serif text-xl md:text-2xl text-foreground mb-5">
                Wat andere vrouwen zeggen
              </p>
              <div className="space-y-4">
                {[
                  {
                    name: "Margriet, 58",
                    text: "Na 3 weken zag ik al verschil. Mijn rimpels zijn zachter en mijn huid voelt steviger. Ik had niet verwacht dat iets vanuit huis zó goed zou werken.",
                    stars: 5,
                  },
                  {
                    name: "Annemieke, 63",
                    text: "Ik heb jarenlang dure crèmes gebruikt zonder resultaat. Dit is het eerste product dat echt doet wat het belooft. Mijn dochter zei dat ik er jonger uitzie!",
                    stars: 5,
                  },
                  {
                    name: "Liesbeth, 54",
                    text: "Makkelijk in gebruik, geen pijn, en het resultaat na 2 maanden is geweldig. Mijn huid glanst weer. Heel blij dat ik het geprobeerd heb.",
                    stars: 5,
                  },
                ].map((review, i) => (
                  <motion.div
                    key={review.name}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 * i }}
                    className="insight-card text-left p-5"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-accent text-sm">{"★".repeat(review.stars)}</span>
                    </div>
                    <p className="font-sans text-base text-foreground/80 leading-relaxed mb-2 italic">
                      "{review.text}"
                    </p>
                    <p className="font-sans text-sm text-accent font-semibold">
                      {review.name}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Safety & trust block */}
            <div className="insight-card mb-8 p-5">
              <p className="font-sans text-sm uppercase tracking-wider text-accent font-bold mb-4 text-center">
                Veilig en betrouwbaar
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "🛡️", label: "Aanbevolen door dermatologen" },
                  { icon: "🇳🇱", label: "Nederlands merk" },
                  { icon: "✓", label: "Veilig voor gevoelige huid" },
                  { icon: "🔬", label: "Klinisch bewezen ingrediënten" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2 text-base font-sans text-foreground">
                    <span className="text-lg flex-shrink-0">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Guarantee — risk reversal */}
            <div className="mb-8 rounded-2xl p-6 text-center border-2" style={{ borderColor: "hsl(140 40% 50%)", background: "hsl(140 30% 97%)" }}>
              <p className="font-serif text-2xl text-foreground mb-2">
                90 dagen uitproberen, niet tevreden? Geld terug.
              </p>
              <p className="font-sans text-base text-foreground/80 leading-relaxed">
                Niet tevreden? Je krijgt je geld volledig terug. Geen vragen, geen gedoe. Je riskeert niets.
              </p>
            </div>

            {/* Urgency */}
            <div className="mb-8 rounded-2xl p-5 text-center" style={{ background: "hsl(38 40% 95%)", border: "1px solid hsl(38 30% 85%)" }}>
              <p className="font-sans text-base text-foreground font-medium">
                ⏳ Je collageenproductie daalt elke dag verder. Hoe eerder je begint, hoe meer resultaat je behaalt.
              </p>
            </div>

            {/* Final CTA */}
            <div className="text-center">
              <a
                href={(() => {
                   const abVar = sessionStorage.getItem('ab_variant') || 'none';
                   const utmContent = abVar === 'A' ? 'huidanalyse_variant_a' : abVar === 'B' ? 'huidanalyse_variant_b' : 'huidanalyse_direct';
                   const base = new URL('https://adelira.nl/products/skinfuse-infusion-kit');
                   base.searchParams.set('utm_campaign', 'huidanalyse');
                   base.searchParams.set('utm_content', utmContent);
                   base.searchParams.set('utm_quiz', 'huidanalyse');
                   return decorateShopUrl(base.toString());
                 })()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cta text-lg md:text-xl px-12 py-6 animate-pulse-glow inline-block"
                onClick={() => {
                  (window as any).dataLayer = (window as any).dataLayer || [];
                  (window as any).dataLayer.push({ event: 'click_to_website' });
                  if (typeof window !== 'undefined' && (window as any).fbq) {
                    (window as any).fbq('track', 'Lead');
                  }
                }}
              >
                Bekijk het product op adelira.nl
              </a>
              <p className="text-foreground/75 font-sans text-base mt-4">
                Geen verplichting · 90 dagen garantie · Gratis behandelplan
              </p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                className="mt-8 flex items-center justify-center gap-2 text-base text-foreground/75 font-sans"
              >
                <span className="text-accent">★★★★★</span>
                <span>Uitstekend 4.8 | 5.842 beoordelingen</span>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Investment;
